import React, { Profiler, type RefObject, useEffect, useMemo, useRef } from 'react';

import { useIntl } from 'react-intl';

import { type GasPayload } from '@atlaskit/analytics-gas-types';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { fg } from '@atlaskit/platform-feature-flags';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Flex, Stack, xcss } from '@atlaskit/primitives';
import { media } from '@atlaskit/primitives/responsive';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import { token } from '@atlaskit/tokens';
import { localStore as knowledgeDiscoveryLocalStore } from '@atlassian/search-ai/local-store';
import { isSearchConfluenceSpace } from '@atlassian/search-client/confluence';
import { isSearchResultJiraIssue } from '@atlassian/search-client/jira';
import {
	type ActivityItemEdge,
	isGraphActivity,
} from '@atlassian/search-client/recent-activities/types';
import { isSearchResultGraphEntity } from '@atlassian/search-client/third-party-graph-entity';
import {
	getRecentQueryObject,
	type RecentSearchItem,
} from '@atlassian/search-common/recent-searches';

import { ApplicationModes } from '../../../common/constants/application-modes';
import {
	type PrimaryProductKey,
	PrimaryProductKeys,
	ProductKeys,
	ProductKeys3P,
} from '../../../common/constants/products';
import {
	DEFAULT_RECENT_AND_SUGGESTED_QUERY_LIMIT,
	PRODUCTS_WITH_INLINE_FILTERS,
	QUICK_FIND_LOADING_HOLD,
	SEARCH_DIALOG_WIDTH_OBSERVER_ID,
} from '../../../common/constants/quick-find';
import { useAppContext } from '../../../common/ui/app-context';
import { Heading } from '../../../common/ui/quick-find/heading';
import { ItemsAnalyticsProvider } from '../../../common/ui/quick-find/items-analytics-provider';
import { SearchResultSkeleton } from '../../../common/ui/quick-find/result-skeleton';
import { SearchDialogAnnouncer } from '../../../common/ui/quick-find/search-dialog-announcer';
import {
	isAutocompleteSuggestionItem,
	isErrorItem,
	isFilterSuggestionItem,
	isHeadingItem,
	isLandingItem,
	isNoResultsItem,
	isPeopleAndTeamsItem,
	isRecentActivityItem,
	isRecentQueryItem,
	isSearchResultItem,
	isSkeletonItem,
	isRecommendedDocumentItem,
	isZeroQueryItem,
	type Item,
	type QuickFindResult,
	type EnrichedRecommendedDocument,
} from '../../../common/ui/quick-find/types';
import { useWidthObserverRegistration } from '../../../common/ui/width-observer';
import { hasAuthorisedSmartLinkConnector } from '../../../common/utils/connectors';
import {
	serveSlClickedOnlyInRecentsExperiment,
	serveGithubWorkItemsRecentsExperiment,
	serveGithubRecentsExperiment,
	serveQfGithubTipBannerExperiment,
	serveQFFilterSuggestionExperiment,
	serveQFZeroQuery3PRecsExperiment,
	serveQFZeroQueryAllocationExperiment,
	serveQuickFind3PBoostingExperiment,
	triggerExposureForEngagementTrackingImprovementsExperiment,
} from '../../../common/utils/experiments';
import { serveJiraNINFpsNLQNavigation } from '../../../common/utils/experiments/experiment-exports';
import { serveGithubLinksHydrationWithoutExposure } from '../../../common/utils/experiments/serve-github-links-hydration-without-exposure';
import { isOnJiraFullPageSearch } from '../../../common/utils/is-on-jira-full-page-search';
import { mergeRefs } from '../../../common/utils/merge-refs';
import { OAuthContainer } from '../../../common/utils/oauth-container';
import { applyThirdPartyBoosting } from '../../../common/utils/quick-find/apply-third-party-boosting';
import { onIntentRouterExperimentExposed } from '../../../common/utils/quick-find/events/intent-router-experiment-exposed';
import { onSearchDialogDismissed } from '../../../common/utils/quick-find/events/search-dialog-dismissed';
import { onSearchDialogShown } from '../../../common/utils/quick-find/events/search-dialog-shown';
import { onSearchResultsRendered } from '../../../common/utils/quick-find/events/search-results-rendered';
import {
	isLoadingState,
	isNotResultsState,
	isPostQueryLoadingOrResults,
	isPostQueryState,
	isPreQueryState,
	State,
} from '../../../common/utils/quick-find/state';
import { useBannerEligibleProductsOrder } from '../../../controllers/3p-smart-link-usage';
import ShowIfEligible from '../../../controllers/application-modes';
import { ErrorBoundary } from '../../../controllers/error-boundary';
import { useOAuthHandlers } from '../../../controllers/oauth';
import {
	getWordCount,
	useQuickFindAnalytics,
	useQuickFindAttributes,
} from '../../../controllers/quick-find/analytics';
import type { QuickFindContent } from '../../../controllers/quick-find/fetch-results/types';
import { useQuickFindPostQueryVitalStats } from '../../../controllers/quick-find/use-quickfind-post-query-vitalstats';
import { useResultsShownCallbacks } from '../../../controllers/quick-find/use-results-shown-callback';
import {
	useQuickFindFooterOnClick,
	useQuickFindResultOnClick,
	useQuickFindResultOnContextMenu,
	useQuickFindResultOnHighlighted,
} from '../../../controllers/quick-find/utils';
import {
	useAvailableProducts,
	usePrimaryProduct,
	useRefreshSearchSessionId,
	useThirdPartyConfigs,
	useUserShardModel,
	useUserShardUI,
} from '../../../controllers/store/bootstrap';
import { useContent } from '../../../controllers/store/quick-find';
import { useSearchActions } from '../../../controllers/store/search';

import { AutocompleteSuggestion } from './autocomplete-suggestion';
import { AssetsUnavailableBanner } from './banners/assets';
import { ConnectedGithubTipBanner } from './banners/github-tip/connected';
import { RovoTipBanner } from './banners/rovo';
import { Divider } from './divider';
import { ErrorScreen } from './error-screen';
import { FeedbackFooter } from './feedback-footer';
import { FilterSuggestion } from './filter-suggestion';
import { QuickFindFilters } from './filters';
import { IntentRouter } from './intent-router';
import { SilentIntentDetection } from './intent-router/silent-intent-detection';
import { JiraAdvancedSearchLink } from './jira/jira-advanced-search-link';
import { JiraSingleSiteSearch } from './jira/jira-single-site-search';
import { LandingScreen } from './landing-screen';
import { messages } from './messages';
import { NoResultsScreen } from './no-results-screen';
import { PeopleAndTeams } from './people-and-teams';
import { ProductEntity } from './product-entity';
import { useQueryFilterResults } from './query-filters';
import { RecentActivity } from './recent-activity';
import { RecentQuery } from './recent-query';
import { RecommendedDocument } from './recommended-document';
import { enrichRecommendedDocumentsWithActivity } from './recommended-document/enrich-recommended-documents-with-activity';
import { SearchPageLink } from './search-page-link';
import { isQueryMatchingActivityKey, isQueryMatchingSearchResultKey } from './utils';
import { ZeroQuery } from './zero-query';

// Minimum query length for intent detection
const MIN_QUERY_LENGTH = 2;

const searchDialogStyles = xcss({
	display: 'flex',
	position: 'absolute',
	flexDirection: 'column',
	backgroundColor: 'elevation.surface.overlay',
	boxShadow: 'elevation.shadow.overlay',
	width: '100%',
});

const searchDialogDefaultStyles = xcss({
	borderRadius: token('radius.small'),
	marginTop: 'space.100',
});

const searchDialogRefreshStyles = xcss({
	borderRadius: token('radius.large'),
	marginTop: 'space.050',
});

const searchDialogWithIntentRouter = xcss({
	top: 'space.0',
	marginTop: 'space.075',
	paddingTop: 'space.500',
	borderRadius: token('radius.large'),
});

const searchResultsSectionStyles = xcss({
	display: 'flex',
	flexDirection: 'column',
	paddingBlockStart: 'space.100',
	paddingBlockEnd: 'space.100',
	gap: 'space.025',
	overflowY: 'auto',
});

const intentRouterBodyStyles = xcss({
	paddingBlockEnd: 'space.100',
});

const footerStyles = xcss({
	flexGrow: 1,
});

const footerContainerStyles = xcss({
	display: 'flex',
	flexDirection: 'column',
	[media.above.lg]: {
		flexDirection: 'row',
	},
});

const footerSpotlightTargetContainerStyles = xcss({
	display: 'flex',
	justifyContent: 'flex-end',
});

const bodyWrapperStyles = xcss({
	display: 'flex',
	gap: 'space.025',
	flexDirection: 'column',
	width: '100%',
});

const footerSpotlightTargetContainerPaddingStyles = xcss({
	marginInlineEnd: 'space.400',
	[media.above.lg]: {
		marginInlineEnd: 'unset',
		paddingBlockStart: 'space.1000',
	},
});

const footerSpotlightTargetStyles = xcss({
	marginBlockStart: 'space.negative.400',
	[media.above.lg]: {
		marginBlockStart: 'unset',
	},
});

const defaultSearchResultsSectionHeightStyles = xcss({
	// 86px for the QF footer + 48px for the page header + 86px bottom gap
	maxHeight: 'calc(100vh - 220px)',
});

const jiraSearchResultsSectionHeightStyles = xcss({
	// 167px for QF footer + 48px for the page header + 15% gap at the bottom
	maxHeight: 'calc(85vh - 215px)',
});

const githubTipBannerStyles = xcss({
	marginInlineStart: 'space.100',
	marginInlineEnd: 'space.100',
	marginBlockStart: 'space.100',
	marginBlockEnd: 'space.100',
});

const githubTipBannerNegativeMarginTopStyles = xcss({
	marginInlineStart: 'space.100',
	marginInlineEnd: 'space.100',
	marginBlockStart: 'space.negative.100',
	marginBlockEnd: 'space.100',
});

const SearchResultsSectionWrapper = ({
	children,
	primaryProduct,
}: {
	children: React.ReactNode;
	primaryProduct: PrimaryProductKey | undefined;
}) => {
	const useDefaultHeight = primaryProduct !== PrimaryProductKeys.Jira;

	return (
		<Box
			xcss={[
				searchResultsSectionStyles,
				useDefaultHeight
					? defaultSearchResultsSectionHeightStyles
					: jiraSearchResultsSectionHeightStyles,
			]}
		>
			{children}
		</Box>
	);
};

type SearchDialogProps = {
	dialogId: string;
	dialogRef: RefObject<HTMLDivElement>;
	inputRef?: RefObject<HTMLInputElement>;
	localQuery: string;
	setLocalQuery: React.Dispatch<React.SetStateAction<string>>;
	PlacementPO?: () => JSX.Element;
	showFilters?: boolean;
	searchRefreshStyling?: boolean;
	beforeContent?: React.ReactNode;
};
export const SearchDialog: ({
	dialogId,
	dialogRef,
	localQuery,
	setLocalQuery,
	inputRef,
	PlacementPO,
	showFilters,
	searchRefreshStyling,
	beforeContent,
}: SearchDialogProps) => React.JSX.Element | null = ({
	dialogId,
	dialogRef,
	localQuery,
	setLocalQuery,
	inputRef,
	PlacementPO,
	showFilters = true,
	searchRefreshStyling = false,
	beforeContent,
}: SearchDialogProps): React.JSX.Element | null => {
	const intl = useIntl();

	const [primaryProduct] = usePrimaryProduct();
	const { retrySearch } = useSearchActions();

	const [thirdPartyConfigs] = useThirdPartyConfigs();
	const availableProducts = useAvailableProducts();
	const bannerEligibleProductsOrder = useBannerEligibleProductsOrder({
		isOnSearchDialog: true,
		thirdPartyConfigs,
		availableProducts,
	});
	const serveUserShardModel = useUserShardModel();
	const serveUserShardUI = useUserShardUI();
	const content = useContent();
	const hasFiredExperimentExposure = useRef(false);

	const serveZeroQuery =
		((primaryProduct === PrimaryProductKeys.Confluence ||
			primaryProduct === PrimaryProductKeys.Atlas ||
			(primaryProduct === PrimaryProductKeys.RovoExtension && fg('quickfind-rovo-extension'))) &&
			fg('kd_zero_query_in_qf_fg')) ||
		primaryProduct === PrimaryProductKeys.Jira;

	const {
		intentRouterEnabled,
		hideFilterSuggestions,
		maxRecentQueryCountWhenNoSuggestions,
		showMatchingWorkItem,
		intentDetectionControlAnalyticsEnabled,
	} = useMemo(() => {
		const serveJiraNINFpsNLQNavigationExp = serveJiraNINFpsNLQNavigation({
			primaryProduct,
			fireLayerExposure: false, // Exposure is fired when a user calls the intent detection API
			locale: intl.locale,
		});

		const maxRecentQueryCountWhenNoSuggestions: 0 | 1 | 2 =
			serveJiraNINFpsNLQNavigationExp?.uiFeatures.maxRecentQueryCountWhenNoSuggestions ??
			DEFAULT_RECENT_AND_SUGGESTED_QUERY_LIMIT;

		return {
			intentRouterEnabled: serveJiraNINFpsNLQNavigationExp?.cohort === 'test',
			intentDetectionControlAnalyticsEnabled:
				serveJiraNINFpsNLQNavigationExp?.cohort === 'control' &&
				(serveJiraNINFpsNLQNavigationExp?.uiFeatures.enableControlIntentDetectionAnalytics ??
					false),
			hideFilterSuggestions:
				serveJiraNINFpsNLQNavigationExp?.uiFeatures.hideFilterSuggestions ?? false,
			maxRecentQueryCountWhenNoSuggestions,
			showMatchingWorkItem:
				serveJiraNINFpsNLQNavigationExp?.uiFeatures.showMatchingWorkItem ?? false,
		};
	}, [primaryProduct, intl.locale]);

	const serveZeroQuery3PRecs = serveQFZeroQuery3PRecsExperiment({ fireExperimentExposure: false });

	const allItems = useBody(
		content,
		serveUserShardUI,
		serveZeroQuery,
		maxRecentQueryCountWhenNoSuggestions,
		hideFilterSuggestions,
		primaryProduct,
		serveZeroQuery3PRecs,
	);

	useResetSessionAnalytics();
	const { fireAnalyticsEvent } = useQuickFindAnalytics();
	const { commonAttributes, nonPrivacySafeAttributes } = useQuickFindAttributes();
	useAnalyticsEvents(fireAnalyticsEvent, commonAttributes, nonPrivacySafeAttributes);

	const resultOnClick = useQuickFindResultOnClick();
	const resultOnContextMenu = useQuickFindResultOnContextMenu();
	const resultOnHighlighted = useQuickFindResultOnHighlighted();
	const footerOnClick = useQuickFindFooterOnClick();

	const queryFiltersBody = useQueryFilterResults({ localQuery, setLocalQuery, inputRef });

	const { isAdminHubAIEnabled, isRovoLLMEnabled } = useAppContext();

	let hasThirdPartyResults = false;

	const localDialogRef = useRef(null);
	useWidthObserverRegistration(SEARCH_DIALOG_WIDTH_OBSERVER_ID, localDialogRef);

	useEffect(() => {
		triggerExposureForEngagementTrackingImprovementsExperiment();
	}, []);

	// Fire exposure for qf_loom_recents experiment when the dialog mounts,
	// but only for users who have Loom access. Jira primary is excluded from
	// the experiment target audience in Statsig, so skip exposure there to
	// keep cohort assignments aligned with where the feature actually ships.
	const hasLoomAccess = availableProducts.includes(ProductKeys.Loom);
	const isJiraPrimary = primaryProduct === PrimaryProductKeys.Jira;
	useEffect(() => {
		if (hasLoomAccess && !isJiraPrimary) {
			// eslint-disable-next-line @atlaskit/platform/use-recommended-utils
			FeatureGates.getExperimentValue<string>('qf_loom_recents', 'cohort', 'not-enrolled', {
				fireExperimentExposure: true,
			});
		}
	}, [hasLoomAccess, isJiraPrimary]);

	useEffect(() => {
		if (isJiraPrimary) {
			// eslint-disable-next-line @atlaskit/platform/use-recommended-utils
			FeatureGates.getExperimentValue<string>(
				'qf_jira_recents_exclude_confluence',
				'cohort',
				'control',
				{
					fireExperimentExposure: true,
				},
			);
		}
	}, [isJiraPrimary]);

	// Fire exposure for both GitHub-in-recents experiments when the dialog mounts,
	// but only for users who have the GitHub full-link connector connected (not needing OAuth)
	const hasGithubConnectorAccess = !!(
		thirdPartyConfigs?.[ProductKeys3P.Github] &&
		!thirdPartyConfigs[ProductKeys3P.Github]?.userNeedsOAuth
	);
	useEffect(() => {
		if (hasGithubConnectorAccess) {
			serveGithubRecentsExperiment({ fireExperimentExposure: true });
			serveGithubWorkItemsRecentsExperiment({ fireExperimentExposure: true });
		}
	}, [hasGithubConnectorAccess]);

	const isEligibleForGithubTipBanner =
		isJiraPrimary &&
		!fg('rovo_search_3p_banner_opt_out') &&
		!!thirdPartyConfigs?.[ProductKeys3P.Github] &&
		thirdPartyConfigs[ProductKeys3P.Github]?.userNeedsOAuth === true;
	useEffect(() => {
		if (isEligibleForGithubTipBanner) {
			serveQfGithubTipBannerExperiment({ fireExperimentExposure: true });
		}
	}, [isEligibleForGithubTipBanner]);

	const githubTipBannerCohort = isEligibleForGithubTipBanner
		? serveQfGithubTipBannerExperiment()
		: 'control';
	const showGithubTipBanner = isEligibleForGithubTipBanner && githubTipBannerCohort !== 'control';
	const disableGninTriggerForGithubTipBanner = githubTipBannerCohort === 'test-keyword-only';

	// Only expose users who can actually be affected by the experiment, i.e.
	// those with at least one authorised smart-link connector. Mirrors the
	// allocation guard in `getFilteredEventTypes`.
	const hasSlConnectorAccess = hasAuthorisedSmartLinkConnector(thirdPartyConfigs);
	useEffect(() => {
		if (hasSlConnectorAccess) {
			serveSlClickedOnlyInRecentsExperiment({ fireExperimentExposure: true });
		}
	}, [hasSlConnectorAccess]);

	useEffect(() => {
		if (
			!hasFiredExperimentExposure.current &&
			localQuery.length >= MIN_QUERY_LENGTH &&
			primaryProduct
		) {
			const exposureResult = serveJiraNINFpsNLQNavigation({
				primaryProduct,
				fireLayerExposure: true,
				locale: intl.locale,
			});

			if (exposureResult?.cohort) {
				fireAnalyticsEvent(
					onIntentRouterExperimentExposed({
						attributes: {
							...commonAttributes,
							searchSessionId: commonAttributes.searchSessionId || '',
							cohort: exposureResult.cohort,
							modelId: exposureResult.modelId,
							queryLength: localQuery.length,
							wordCount: getWordCount(localQuery),
						},
					}),
				);
			}

			hasFiredExperimentExposure.current = true;
		}
	}, [
		primaryProduct,
		fireAnalyticsEvent,
		commonAttributes,
		nonPrivacySafeAttributes,
		localQuery,
		intl.locale,
	]);

	const serveGithubLinksHydration = serveGithubLinksHydrationWithoutExposure();

	if (!content) {
		return null;
	}

	const swapQuickFindCTAs =
		isOnJiraFullPageSearch() &&
		primaryProduct &&
		primaryProduct === PrimaryProductKeys.Jira &&
		fg('jira_search_fps_cta_swap');

	const { state } = content;

	const body: React.JSX.Element[] = [];
	const intentRouterBody: React.JSX.Element[] = [];
	let searchResultItemBody: React.JSX.Element[] | undefined;
	let workItemKeyMatch: React.JSX.Element | undefined;

	for (const content of allItems) {
		if (isErrorItem(content)) {
			body.push(<ErrorScreen key={content.id} onRetry={retrySearch} />);
		}

		if (isLandingItem(content)) {
			body.push(<LandingScreen key={content.id} />);
		}

		if (isNoResultsItem(content)) {
			body.push(<NoResultsScreen key={content.id} onRetry={retrySearch} />);
		}

		if (isHeadingItem(content)) {
			body.push(
				<Heading key={content.id} id={content.id} includeInAnalytics={content.includeInAnalytics}>
					{content.data}
				</Heading>,
			);
		}

		if (isSkeletonItem(content)) {
			body.push(<SearchResultSkeleton key={content.id} />);
		}

		if (isRecentQueryItem(content)) {
			(intentRouterEnabled ? intentRouterBody : body).push(
				<RecentQuery key={content.id} result={content.data} state={state} />,
			);
		}

		if (isAutocompleteSuggestionItem(content)) {
			(intentRouterEnabled ? intentRouterBody : body).push(
				<AutocompleteSuggestion key={content.id} result={content.data} state={state} />,
			);
		}

		if (isRecentActivityItem(content)) {
			const activityProps = {
				key: content.id,
				result: content.data,
				state,
				onClick: resultOnClick,
				onContextMenu: resultOnContextMenu,
				onHighlighted: resultOnHighlighted,
				serveGithubLinksHydration,
			};
			const activity = <RecentActivity {...activityProps} />;
			if (
				intentRouterEnabled &&
				showMatchingWorkItem &&
				isQueryMatchingActivityKey(localQuery, content)
			) {
				workItemKeyMatch = (
					<RecentActivity
						{...activityProps}
						key="intent-router-matching-work-item"
						resultType="intentRouterWorkItem"
					/>
				);
			}

			if (serveUserShardUI) {
				if (!searchResultItemBody) {
					searchResultItemBody = [];
					body.push(
						<Profiler
							id="QuickFindSearchDialogResults"
							onRender={(_, phase, actualDuration) => {
								if (phase === 'mount' && localQuery.length) {
									fireAnalyticsEvent(
										onSearchResultsRendered({
											attributes: {
												...commonAttributes,
												durationMs: actualDuration,
												hasThirdPartyResults,
											},
											nonPrivacySafeAttributes,
										}),
									);
								}
							}}
						>
							{searchResultItemBody}
						</Profiler>,
					);
				}
				searchResultItemBody.push(activity);
			} else {
				body.push(activity);
			}
		}

		if (isRecommendedDocumentItem(content)) {
			body.push(
				<RecommendedDocument
					key={content.id}
					result={content.data}
					state={state}
					onClick={resultOnClick}
					onContextMenu={resultOnContextMenu}
					onHighlighted={resultOnHighlighted}
				/>,
			);
		}

		if (isZeroQueryItem(content) && serveZeroQuery) {
			(intentRouterEnabled ? intentRouterBody : body).push(
				<ZeroQuery
					key={content.id}
					result={content.data}
					product={content.product}
					avatarUrl={content.avatarUrl}
					state={state}
					zeroQueryType={content.zeroQueryType}
					zeroQueryPosition={allItems.indexOf(content)}
					// accountId={content.accountId}
					// dateRange={content.dateRange}
				/>,
			);
		}

		if (isFilterSuggestionItem(content)) {
			body.push(<FilterSuggestion key={content.id} result={content.data} state={state} />);
		}

		if (isSearchResultItem(content)) {
			const productEntityProps = {
				key: content.id,
				result: content.data,
				state,
				onClick: resultOnClick,
				onContextMenu: resultOnContextMenu,
				onHighlighted: resultOnHighlighted,
			};
			const productEntity = <ProductEntity {...productEntityProps} />;

			if (
				intentRouterEnabled &&
				showMatchingWorkItem &&
				isQueryMatchingSearchResultKey(localQuery, content)
			) {
				workItemKeyMatch = (
					<ProductEntity
						{...productEntityProps}
						resultType="intentRouterWorkItem"
						key="intent-router-matching-work-item"
					/>
				);
			}

			if (serveUserShardModel) {
				if (!searchResultItemBody) {
					searchResultItemBody = [];
					body.push(
						<Profiler
							id="QuickFindSearchDialogResults"
							onRender={(_, phase, actualDuration) => {
								if (phase === 'mount' && localQuery.length) {
									fireAnalyticsEvent(
										onSearchResultsRendered({
											attributes: {
												...commonAttributes,
												durationMs: actualDuration,
												hasThirdPartyResults,
											},
											nonPrivacySafeAttributes,
										}),
									);
								}
							}}
						>
							{searchResultItemBody}
						</Profiler>,
					);
				}

				searchResultItemBody.push(productEntity);
				if (isSearchResultGraphEntity(content.data)) {
					hasThirdPartyResults = true;
				}
			} else {
				body.push(productEntity);
			}
		}

		if (isPeopleAndTeamsItem(content)) {
			body.push(
				<PeopleAndTeams
					key={content.id}
					result={content.data}
					state={state}
					onClick={resultOnClick}
					onContextMenu={resultOnContextMenu}
					onHighlighted={resultOnHighlighted}
				/>,
			);
		}
	}

	const footer = (() => {
		// Post-query state with Confluence, Focus, or RovoExtension
		if (
			(primaryProduct === PrimaryProductKeys.Confluence ||
				primaryProduct === PrimaryProductKeys.Focus ||
				(primaryProduct === PrimaryProductKeys.RovoExtension && fg('quickfind-rovo-extension'))) &&
			isPostQueryState(state)
		) {
			return (
				<>
					<ShowIfEligible requiredMode={ApplicationModes.Rovo}>
						<Divider />
						<SearchPageLink state={state} onClick={footerOnClick} />
					</ShowIfEligible>
					<Divider />
					<FeedbackFooter />
				</>
			);
		}

		if (primaryProduct === PrimaryProductKeys.Jira) {
			return (
				<>
					<JiraSingleSiteSearch state={state} onClick={footerOnClick} />
					{!intentRouterEnabled && (
						<>
							{swapQuickFindCTAs ? (
								<>
									<SearchPageLink state={state} onClick={footerOnClick} />
									<JiraAdvancedSearchLink state={state} onClick={footerOnClick} />
								</>
							) : (
								<>
									<JiraAdvancedSearchLink state={state} onClick={footerOnClick} />
									<SearchPageLink state={state} onClick={footerOnClick} />
								</>
							)}
						</>
					)}
					<Divider />
					<FeedbackFooter />
				</>
			);
		}

		// Pre-query footer
		return (
			<>
				<ShowIfEligible requiredMode={ApplicationModes.Rovo}>
					<Divider />
					<SearchPageLink state={state} onClick={footerOnClick} />
				</ShowIfEligible>
				<Divider />
				<FeedbackFooter />
			</>
		);
	})();

	const isAIEnabled = isRovoLLMEnabled || isAdminHubAIEnabled;
	const displayFilters = PRODUCTS_WITH_INLINE_FILTERS.includes(primaryProduct);
	const nonResultsBody = isNotResultsState(state);
	const localQueryEmpty = localQuery.trim().length === 0;
	const isLoading = isLoadingState(state);

	const renderGithubTipBanner = (withTrailingDivider: boolean, withNegativeMarginTop: boolean = false) =>
		showGithubTipBanner ? (
			<ShowIfEligible requiredMode={ApplicationModes.Rovo}>
				<Box xcss={withNegativeMarginTop ? githubTipBannerNegativeMarginTopStyles : githubTipBannerStyles}>
				<ConnectedGithubTipBanner
						query={localQuery}
						disableGninTrigger={disableGninTriggerForGithubTipBanner}
					/>
				</Box>
				{withTrailingDivider && <Divider />}
			</ShowIfEligible>
		) : null;

	return (
		<Box
			xcss={[
				searchDialogStyles,
				// remove searchDialogRefreshStyles and searchDialogDefaultStyles when cleaning up intentRouterEnabled as it overrides them
				searchRefreshStyling ? searchDialogRefreshStyles : searchDialogDefaultStyles,
				intentRouterEnabled && searchDialogWithIntentRouter,
			]}
			aria-label={intl.formatMessage(messages.searchDialogAriaLabel)}
			testId={'search-dialog'}
			ref={mergeRefs(dialogRef, localDialogRef)}
			id={dialogId}
		>
			{beforeContent}
			<ItemsAnalyticsProvider allItems={allItems}>
				{intentRouterEnabled && (
					<>
						{!localQueryEmpty && (
							<IntentRouter
								isLoading={isLoading}
								showMatchingWorkItem={showMatchingWorkItem}
								matchingItem={workItemKeyMatch}
								minQueryLength={MIN_QUERY_LENGTH}
								query={localQuery}
							/>
						)}

						{fg('rovo_quick_find_aria_live_announcements') ? (
							<Box
								xcss={[bodyWrapperStyles, intentRouterBodyStyles]}
								{...(!nonResultsBody &&
									intentRouterBody.length > 0 && {
										role: 'listbox',
										'aria-label': intl.formatMessage(messages.searchResultsHeading),
									})}
							>
								{intentRouterBody}
							</Box>
						) : (
							<Stack
								{...(localQueryEmpty && intentRouterBody.length > 0 ? { role: 'list' } : {})}
								space="space.025"
								xcss={intentRouterBodyStyles}
							>
								{intentRouterBody}
							</Stack>
						)}
						{renderGithubTipBanner(false, true)}
						<Divider />
					</>
				)}
				{!intentRouterEnabled && renderGithubTipBanner(true)}
				{intentDetectionControlAnalyticsEnabled && !localQueryEmpty && (
					<SilentIntentDetection query={localQuery} minQueryLength={MIN_QUERY_LENGTH} />
				)}
				{queryFiltersBody ? (
					<SearchResultsSectionWrapper primaryProduct={primaryProduct}>
						{queryFiltersBody}
					</SearchResultsSectionWrapper>
				) : (
					<>
						<SearchResultsSectionWrapper primaryProduct={primaryProduct}>
							{primaryProduct === PrimaryProductKeys.Assets && !fg('assets_rovo_search_all') && (
								<AssetsUnavailableBanner />
							)}
							<ShowIfEligible requiredMode={ApplicationModes.Rovo}>
								{isPreQueryState(state) && (
									<ErrorBoundary section="QuickFindSearchDialogOAuthContainer">
										<OAuthContainer
											isOnSearchDialog
											thirdPartyConfigs={thirdPartyConfigs}
											eligibleProducts={bannerEligibleProductsOrder}
											useOAuthHandlers={useOAuthHandlers}
											fireAnalyticsEvent={fireAnalyticsEvent}
										/>
									</ErrorBoundary>
								)}
								{showRovoTipBanner(state, isAIEnabled) && !serveZeroQuery && <RovoTipBanner />}
								{PlacementPO && <PlacementPO />}
								{(displayFilters || isPostQueryState(state)) && showFilters && (
									<Flex direction="column">
										<QuickFindFilters />
										<Box paddingInlineStart="space.400">
											<Box id="spotlight-multi-step-on-trigger-step-1" />
										</Box>
									</Flex>
								)}
							</ShowIfEligible>
							{isLoading && <UFOLoadHold name={QUICK_FIND_LOADING_HOLD} />}
							{fg('rovo_quick_find_aria_live_announcements') ? (
								<Box
									xcss={bodyWrapperStyles}
									{...(!nonResultsBody && {
										role: 'listbox',
										'aria-label': intl.formatMessage(messages.searchResultsHeading),
									})}
								>
									{body}
								</Box>
							) : (
								<Stack {...(!nonResultsBody ? { role: 'list' } : {})} space="space.025">
									{body}
								</Stack>
							)}
						</SearchResultsSectionWrapper>
						<Box xcss={footerContainerStyles}>
							<Box xcss={footerStyles}>{footer}</Box>
							<Box
								xcss={[
									footerSpotlightTargetContainerStyles,
									!intentRouterEnabled && footerSpotlightTargetContainerPaddingStyles,
								]}
							>
								<Box
									xcss={footerSpotlightTargetStyles}
									id="spotlight-multi-step-on-trigger-step-2"
								/>
							</Box>
						</Box>
					</>
				)}
			</ItemsAnalyticsProvider>
			{fg('rovo_quick_find_aria_live_announcements') && (
				<SearchDialogAnnouncer isLoading={isLoading} />
			)}
		</Box>
	);
};

export const deduplicateRecentActivitiesAgainstRecommendations = (
	recentActivities: QuickFindResult<ActivityItemEdge>[],
	recommendations: QuickFindResult<EnrichedRecommendedDocument>[],
): QuickFindResult<ActivityItemEdge>[] => {
	return recentActivities
		.filter((activity) => activity?.data?.node?.object?.data !== null)
		.filter((activity) => {
			const thirdPartyAri = activity?.data?.node?.object?.id;
			return !recommendations.some((recommendation) => recommendation.id === thirdPartyAri);
		});
};

const setRecentActivityCount = (filteredActivities: any[]) => {
	if (fg('kd_recent_activity_intent')) {
		knowledgeDiscoveryLocalStore.setString(
			'recent-activity-count',
			filteredActivities.length.toString(),
		);
	}
};

const getJiraSuggestionsAndRecentQueries = ({
	recentQueries: inputRecentQueries,
	zeroQueries: inputZeroQueries,
	maxRecentQueryCountWhenNoSuggestions,
}: {
	recentQueries: QuickFindResult<RecentSearchItem>[];
	zeroQueries: QuickFindResult<string>[];
	maxRecentQueryCountWhenNoSuggestions?: 0 | 1 | 2;
}) => {
	const suggestionsAndRecentQueries: QuickFindResult<string | RecentSearchItem>[] = [];
	const zeroQueries = inputZeroQueries.slice(0, 1);
	const dedupedRecentQueries = inputRecentQueries
		.filter(
			(recentQuery) =>
				!zeroQueries.map((q) => q.data).includes(getRecentQueryObject(recentQuery.data).query),
		)
		.slice(0, 1);

	if (zeroQueries.length > 0 && dedupedRecentQueries.length > 0) {
		suggestionsAndRecentQueries.push(...zeroQueries);
		suggestionsAndRecentQueries.push(...dedupedRecentQueries);
	} else if (zeroQueries.length > 0) {
		// show up to 2 zero queries if no deduped recent queries
		suggestionsAndRecentQueries.push(...inputZeroQueries.slice(0, 2));
	} else if (dedupedRecentQueries.length > 0) {
		// When no zero queries, use maxRecentQueryCountWhenNoSuggestions if provided
		// Otherwise show up to 2 recent queries (default behavior)
		const recentQueryLimit = maxRecentQueryCountWhenNoSuggestions ?? 2;
		suggestionsAndRecentQueries.push(...inputRecentQueries.slice(0, recentQueryLimit));
	}
	return suggestionsAndRecentQueries;
};

export const getZeroQueryAllocationConfig = (
	serveUserDocument: boolean,
	serveZQAllocationExperiment: boolean,
): {
	maxRecommendationsCount: number;
	totalSlots: number;
} => {
	if (serveZQAllocationExperiment) {
		return {
			maxRecommendationsCount: 3,
			totalSlots: 8,
		};
	}

	if (serveUserDocument) {
		return {
			maxRecommendationsCount: 4,
			totalSlots: 7,
		};
	}

	return {
		maxRecommendationsCount: 0,
		totalSlots: 7,
	};
};

export const useBody: (
	content: QuickFindContent,
	serveUserShardUI: boolean,
	serveZeroQuery: boolean,
	maxRecentQueryCountWhenNoSuggestions: 0 | 1 | 2,
	hideFilterSuggestions: boolean,
	primaryProduct?: PrimaryProductKey,
	serveZeroQuery3PRecs?: boolean,
) => Item[] = (
	content: QuickFindContent,
	serveUserShardUI: boolean,
	serveZeroQuery: boolean,
	maxRecentQueryCountWhenNoSuggestions: 0 | 1 | 2,
	hideFilterSuggestions: boolean,
	primaryProduct?: PrimaryProductKey,
	serveZeroQuery3PRecs?: boolean,
) => {
	const intl = useIntl();

	const { onSearchResultsShown, handleRecentActivitiesCallback } = useResultsShownCallbacks();
	const serveFilterSuggestion = serveQFFilterSuggestionExperiment(primaryProduct, {
		fireLayerExposure: false,
	});

	const userRelevantDocumentsEnabled = useMemo(() => {
		return !serveUserShardUI && primaryProduct === PrimaryProductKeys.Confluence;
	}, [primaryProduct, serveUserShardUI]);

	const serve3PBoosting = useMemo(() => {
		return (
			userRelevantDocumentsEnabled &&
			serveQuickFind3PBoostingExperiment({ fireExperimentExposure: true })
		);
	}, [userRelevantDocumentsEnabled]);

	const serveZQAllocationExperiment = useMemo(() => {
		return (
			userRelevantDocumentsEnabled &&
			serveQFZeroQueryAllocationExperiment({ fireExperimentExposure: true })
		);
	}, [userRelevantDocumentsEnabled]);

	return useMemo(() => {
		if (!content) {
			return [];
		}

		const { state } = content;

		const items: Item[] = [];

		const recentsHeadingItem = {
			id: 'recent-activities',
			type: 'heading' as const,
			data: intl.formatMessage(messages.recentsHeading),
		};

		const recommendationsHeadingItem = {
			id: 'recent-activities',
			type: 'heading' as const,
			data: intl.formatMessage(messages.recommendationsHeading),
		};

		const recommendedHeadingItem = {
			id: 'recommended',
			type: 'heading' as const,
			data: intl.formatMessage(messages.recommendedHeading),
		};

		const headingItem = userRelevantDocumentsEnabled
			? recommendationsHeadingItem
			: recentsHeadingItem;

		const searchResultsHeadingItem = {
			id: 'search-results',
			type: 'heading' as const,
			data: intl.formatMessage(
				serveUserShardUI ? messages.userShardHeading : messages.searchResultsHeading,
			),
		};

		let filteredRecentActivities: QuickFindResult<ActivityItemEdge>[] = [];

		if (state === State.PREQUERY_ERROR) {
			items.push({ id: 'error', type: 'error' });
		}

		if (state === State.PREQUERY_LOADING) {
			items.push(...content.recentQueries.slice(0, 3));
			items.push(headingItem);
			for (let i = 0; i < 7; i++) {
				items.push({ id: `skeleton-${i}`, type: 'skeleton' });
			}
		}

		if (state === State.PREQUERY_NO_RESULTS) {
			items.push({ id: 'landing', type: 'landing' });
		}

		if (state === State.PREQUERY_RESULTS) {
			let zeroQueriesCount = Number(maxRecentQueryCountWhenNoSuggestions);
			if (serveFilterSuggestion && !hideFilterSuggestions && content.filterSuggestions.length > 0) {
				// filter suggestion replaces one of the zero queries
				zeroQueriesCount = Math.max(0, maxRecentQueryCountWhenNoSuggestions - 1);
				items.push(...content.filterSuggestions);
			}

			if (serveZeroQuery) {
				const visibleZeroQueries = content.zeroQueries.slice(0, zeroQueriesCount);
				// Only show recent queries if maxRecentQueryCountWhenNoSuggestions > 0
				let dedupedVisibleRecentQueries: QuickFindResult<RecentSearchItem>[] = [];
				if (maxRecentQueryCountWhenNoSuggestions > 0) {
					dedupedVisibleRecentQueries = content.recentQueries
						.filter(
							(recentSearch) =>
								!visibleZeroQueries.some((zeroQuery) => zeroQuery.data === recentSearch.data.query),
						)
						.slice(0, 1); // Always show up to 1 recent query when zero queries are shown
				}
				items.push(...visibleZeroQueries);
				items.push(...dedupedVisibleRecentQueries);
			} else {
				// When zero queries are not served, use maxRecentQueryCountWhenNoSuggestions; otherwise show 3
				// NOTE: This only applies to Jira within the serveJiraNINFpsNLQNavigation intent detection experiment
				const recentQueryLimit =
					primaryProduct === PrimaryProductKeys.Jira &&
					maxRecentQueryCountWhenNoSuggestions !== DEFAULT_RECENT_AND_SUGGESTED_QUERY_LIMIT
						? maxRecentQueryCountWhenNoSuggestions
						: 3;
				items.push(...content.recentQueries.slice(0, recentQueryLimit));
			}

			if (userRelevantDocumentsEnabled) {
				const { maxRecommendationsCount, totalSlots } = getZeroQueryAllocationConfig(
					userRelevantDocumentsEnabled,
					serveZQAllocationExperiment,
				);

				const recommendations = serveZeroQuery3PRecs
					? content.recommendedDocuments
					: content.userDocuments;
				if (recommendations.length + content.recentActivities.length > 0) {
					const enrichedRecommendations = enrichRecommendedDocumentsWithActivity(
						recommendations,
						content.recentActivities,
					);
					const filteredRecommendedDocuments = enrichedRecommendations.slice(
						0,
						maxRecommendationsCount,
					);
					const remainingSlots = totalSlots - filteredRecommendedDocuments.length;
					const dedupedRecentActivities = deduplicateRecentActivitiesAgainstRecommendations(
						content.recentActivities,
						filteredRecommendedDocuments,
					);

					if (serve3PBoosting) {
						filteredRecentActivities = applyThirdPartyBoosting(
							dedupedRecentActivities,
							remainingSlots,
						);
					} else {
						filteredRecentActivities = dedupedRecentActivities.slice(0, remainingSlots);
					}

					if (serveZQAllocationExperiment) {
						// Separate heading between recommendations and recent
						if (filteredRecommendedDocuments.length > 0) {
							items.push(recommendedHeadingItem, ...filteredRecommendedDocuments);
						}

						if (filteredRecentActivities.length > 0) {
							items.push(recentsHeadingItem, ...filteredRecentActivities);
						}
					} else {
						// Combine recommendations and recent under one heading
						items.push(
							recommendationsHeadingItem,
							...filteredRecommendedDocuments,
							...filteredRecentActivities,
						);
					}
				}
			} else {
				if (content.recentActivities.length > 0) {
					items.push(recentsHeadingItem);
					filteredRecentActivities = content.recentActivities
						.filter((activity) => activity.data.node.object.data !== null)
						.slice(0, 7);
					items.push(...filteredRecentActivities);
				}
			}
			setRecentActivityCount(filteredRecentActivities);
		}

		if (state === State.POSTQUERY_ERROR) {
			items.push({ id: 'error', type: 'error' });
		}

		if (state === State.POSTQUERY_LOADING) {
			if (primaryProduct === PrimaryProductKeys.Jira) {
				items.push(
					...getJiraSuggestionsAndRecentQueries({
						recentQueries: content.recentQueries,
						zeroQueries: content.zeroQueries,
						maxRecentQueryCountWhenNoSuggestions,
					}),
				);
			}

			if (userRelevantDocumentsEnabled) {
				const recommendations = serveZeroQuery3PRecs
					? content.recommendedDocuments
					: content.userDocuments;
				if (recommendations.length + content.recentActivities.length > 0) {
					items.push(recentsHeadingItem);
					const enrichedRecommendedDocuments = enrichRecommendedDocumentsWithActivity(
						recommendations,
						content.recentActivities,
					);
					const filteredRecommendedDocuments = enrichedRecommendedDocuments.slice(0, 5);
					filteredRecentActivities = deduplicateRecentActivitiesAgainstRecommendations(
						content.recentActivities,
						filteredRecommendedDocuments,
					).slice(0, 5 - filteredRecommendedDocuments.length);
					items.push(...filteredRecommendedDocuments);
					items.push(...filteredRecentActivities);
				}
			} else {
				if (!serveUserShardUI && content.recentActivities.length > 0) {
					items.push(recentsHeadingItem);
					filteredRecentActivities = content.recentActivities
						.filter((activity) => activity.data.node.object.data !== null)
						.slice(0, 5);
					items.push(...filteredRecentActivities);
				}
			}
			setRecentActivityCount(filteredRecentActivities);
			items.push({ ...searchResultsHeadingItem, includeInAnalytics: false });
			const numberOfSkeletons = primaryProduct === PrimaryProductKeys.Jira ? 10 : 5;
			for (let i = 0; i < numberOfSkeletons; i++) {
				items.push({ id: `skeleton-${i}`, type: 'skeleton' });
			}
		}

		if (state === State.POSTQUERY_NO_RESULTS) {
			items.push({ id: 'no-results', type: 'no-results' });
		}

		if (state === State.POSTQUERY_RESULTS) {
			if (
				'autocompleteSuggestions' in content &&
				Array.isArray(content.autocompleteSuggestions) &&
				fg('fe_quickfind_autocomplete_suggestions')
			) {
				const autocompleteSuggestions = content.autocompleteSuggestions;
				if (autocompleteSuggestions.length > 0) {
					const autocompleteSuggestionsHeadingItem = {
						id: 'autocomplete-suggestions',
						type: 'heading' as const,
						data: 'Autocomplete suggestions',
						includeInAnalytics: false,
					};
					items.push(autocompleteSuggestionsHeadingItem);

					autocompleteSuggestions.slice(0, 3).forEach((suggestion, index: number) => {
						items.push({
							id: `autocomplete-suggestion-${index}`,
							type: 'autocomplete-suggestion',
							data: suggestion.data,
						});
					});
				}
			}

			if (primaryProduct === PrimaryProductKeys.Jira) {
				items.push(
					...getJiraSuggestionsAndRecentQueries({
						recentQueries: content.recentQueries,
						zeroQueries: content.zeroQueries,
						maxRecentQueryCountWhenNoSuggestions,
					}),
				);
			}

			filteredRecentActivities = content.recentActivities
				.filter((activity) => activity.data.node.object.data !== null)
				.slice(0, 5);

			const enrichedRecommendedDocuments = enrichRecommendedDocumentsWithActivity(
				serveZeroQuery3PRecs ? content.recommendedDocuments : content.userDocuments,
				content.recentActivities,
			);
			const filteredRecommendedDocuments = enrichedRecommendedDocuments.slice(0, 5);

			if (userRelevantDocumentsEnabled) {
				if (filteredRecommendedDocuments.length + content.recentActivities.length > 0) {
					items.push(recentsHeadingItem);
					filteredRecentActivities = deduplicateRecentActivitiesAgainstRecommendations(
						content.recentActivities,
						filteredRecommendedDocuments,
					).slice(0, 5 - filteredRecommendedDocuments.length);
					items.push(...filteredRecommendedDocuments);
					items.push(...filteredRecentActivities);
				}
			} else if (!serveUserShardUI) {
				if (filteredRecentActivities.length > 0) {
					items.push(recentsHeadingItem);
					items.push(...filteredRecentActivities);
				}
			}

			if (!serveUserShardUI) {
				setRecentActivityCount(filteredRecentActivities);
			}

			let uniqueMatchedRecentActivities: QuickFindResult<ActivityItemEdge>[] = [];
			let dedupedSearchResults: QuickFindResult<any>[] = [];
			const seenIds = new Set<string>();

			if (serveUserShardUI) {
				uniqueMatchedRecentActivities = filteredRecentActivities
					.filter((activity) => {
						const activityId = isGraphActivity(activity.data.node.object)
							? activity.data.node.object.data?.id
							: activity.id;
						const isInSearchResults = content.searchResults.some(
							(searchResult) => searchResult.id === activityId,
						);
						return !isInSearchResults;
					})
					.slice(0, 3);
				dedupedSearchResults = content.searchResults.filter((searchResult) => {
					const isDuplicatedResult = seenIds.has(searchResult.id);
					if (!isDuplicatedResult) {
						seenIds.add(searchResult.id);
					}
					return searchResult.id && !isDuplicatedResult;
				});
			} else {
				dedupedSearchResults = content.searchResults.filter((searchResult) => {
					const isInRecentActivities = filteredRecentActivities.some((recentlyViewedResult) => {
						const activityObject = recentlyViewedResult.data.node.object;
						const id = isGraphActivity(activityObject)
							? activityObject.data?.id
							: recentlyViewedResult.id;
						return id === searchResult.id;
					});

					const isInRecommendedDocuments = filteredRecommendedDocuments.some(
						(recommendation) => recommendation.id === searchResult.id,
					);

					const isDuplicateResult = seenIds.has(searchResult.id);
					if (!isDuplicateResult) {
						seenIds.add(searchResult.id);
					}

					return (
						searchResult.id &&
						!isInRecentActivities &&
						!isDuplicateResult &&
						!isInRecommendedDocuments
					);
				});
			}

			onSearchResultsShown(dedupedSearchResults);

			// The way product entities are shown depend on the primary product
			if (primaryProduct === PrimaryProductKeys.Confluence) {
				const confluenceSpaceResult = dedupedSearchResults.find((result) =>
					isSearchConfluenceSpace(result.data),
				);

				const showSearchResultsSection = Boolean(
					dedupedSearchResults.length +
					content.peopleAndTeams.length +
					uniqueMatchedRecentActivities.length,
				);

				if (showSearchResultsSection) {
					items.push(searchResultsHeadingItem);
					const filteredSearchResults = dedupedSearchResults.filter(
						(result) => !isSearchConfluenceSpace(result.data),
					);
					if (serveUserShardUI) {
						const fusionResults: { item: Item; timestamp: number }[] = [];
						fusionResults.push(
							...uniqueMatchedRecentActivities.map((activity) => ({
								item: activity,
								timestamp: Date.parse(activity.data.node.event.timestamp),
							})),
						);
						const maxSearchResultCount =
							(confluenceSpaceResult ? 10 : 11) - uniqueMatchedRecentActivities.length;
						fusionResults.push(
							...filteredSearchResults.slice(0, maxSearchResultCount).map((result) => ({
								item: result,
								timestamp: result.data.latestUserActionTs
									? Date.parse(result.data.latestUserActionTs)
									: 0,
							})),
						);
						fusionResults.sort((a, b) => b.timestamp - a.timestamp);
						items.push(...fusionResults.map((itemWithTimestamp) => itemWithTimestamp.item));
					} else {
						items.push(...filteredSearchResults.slice(0, confluenceSpaceResult ? 5 : 6));
					}
					confluenceSpaceResult && items.push(confluenceSpaceResult);
					items.push(...content.peopleAndTeams.slice(0, 2));
					items.push(...content.recentQueries.slice(0, 1));
				}
			} else if (primaryProduct === PrimaryProductKeys.Jira) {
				const showSearchResultsSection = Boolean(dedupedSearchResults.length);

				if (showSearchResultsSection) {
					const issueResults = dedupedSearchResults.filter((result) =>
						isSearchResultJiraIssue(result.data),
					);

					const nonIssueResults = dedupedSearchResults.filter(
						(result) => !isSearchResultJiraIssue(result.data),
					);

					const numberOfJiraIssues = 10;
					items.push(searchResultsHeadingItem);
					items.push(...issueResults.slice(0, numberOfJiraIssues));
					items.push(...nonIssueResults.slice(0, 3));
				}
			} else {
				const showSearchResultsSection = Boolean(
					dedupedSearchResults.length + content.peopleAndTeams.length,
				);

				if (showSearchResultsSection) {
					items.push(searchResultsHeadingItem);
					items.push(...dedupedSearchResults.slice(0, 6));
					items.push(...content.peopleAndTeams.slice(0, 2));
					items.push(...content.recentQueries.slice(0, 1));
				}
			}
		}

		/* We don't want to interrupt the handleRecentActivitiesCallback with POSTQUERY_RESULTS
		 * as it's already handled in POSTQUERY_LOADING'
		 */
		if (state !== State.POSTQUERY_RESULTS) {
			handleRecentActivitiesCallback(state, filteredRecentActivities);
		}

		return items;
	}, [
		content,
		intl,
		userRelevantDocumentsEnabled,
		serveUserShardUI,
		maxRecentQueryCountWhenNoSuggestions,
		hideFilterSuggestions,
		serveFilterSuggestion,
		serveZeroQuery,
		serve3PBoosting,
		serveZQAllocationExperiment,
		primaryProduct,
		onSearchResultsShown,
		handleRecentActivitiesCallback,
		serveZeroQuery3PRecs,
	]);
};

const useAnalyticsEvents = (
	fireAnalyticsEvent: (payload: GasPayload) => void,
	commonAttributes: Record<string, any>,
	nonPrivacySafeAttributes: Record<string, any>,
) => {
	const { abortTask, isTaskActive } = useQuickFindPostQueryVitalStats();

	useEffect(() => {
		fireAnalyticsEvent(
			onSearchDialogShown({ attributes: commonAttributes, nonPrivacySafeAttributes }),
		);

		return () => {
			fireAnalyticsEvent(
				onSearchDialogDismissed({ attributes: commonAttributes, nonPrivacySafeAttributes }),
			);

			// Fire VitalStats taskAbort if a task is active when dialog is dismissed
			// Note: abortTask handles deferring the analytics event internally
			if (isTaskActive()) {
				abortTask('dialog_closed_during_network');
			}
		};
		// Only want these events to fire when the dialog opens and closes
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
};

const useResetSessionAnalytics = () => {
	const refreshSearchSessionId = useRefreshSearchSessionId();
	const { resetQueryVersion } = useSearchActions();

	useEffect(() => {
		refreshSearchSessionId();
		resetQueryVersion();
	}, [refreshSearchSessionId, resetQueryVersion]);
};

export const showRovoTipBanner: (state: State, isAIEnabled?: boolean) => boolean = (
	state: State,
	isAIEnabled: boolean = false,
): boolean => {
	const isCorrectState = isPreQueryState(state) || isPostQueryLoadingOrResults(state);

	if (fg('rovo_search_updated_entitlement_checks_for_team_25')) {
		return isCorrectState && isAIEnabled;
	}

	return isCorrectState;
};
